import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Symbol Coding — vue React staircase + debrief (Wave M2 cyanotype).
 *
 * Pattern :
 *   - useSyncExternalStore sur adapter.subscribe pour le rendu.
 *   - Header trial count + stop button.
 *   - Toujours visible : la "key" (mini grille 9 paires symbole|digit).
 *   - Au centre : le digit cible.
 *   - En bas : 4 options symboles cliquables.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
import { SYMBOLS_KEY, } from './adapter';
function MiniKey({ symKey }) {
    return (_jsx("div", { className: "flex w-full max-w-[320px] flex-wrap items-center justify-center gap-s1 border border-cyan-border-faint bg-cyan-surface p-s2", children: symKey.map((symIdx, i) => (_jsxs("div", { className: "flex items-center gap-s1 px-s2 py-s1", children: [_jsx("span", { className: "font-display text-[16px] text-cyan-text", children: SYMBOLS_KEY[symIdx] }), _jsx("span", { className: "font-mono text-[10px] text-cyan-text-secondary", children: i + 1 })] }, `mini-${i}`))) }));
}
export function SymbolCodingStaircaseView({ adapter, onAbort, }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handlePick = useCallback((idx) => adapter.handleAnswer(idx), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const showOptions = adapterState.subPhase === 'awaiting_input';
    const showDigit = adapterState.subPhase === 'stimulus' ||
        adapterState.subPhase === 'awaiting_input' ||
        adapterState.subPhase === 'feedback';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsx(MiniKey, { symKey: adapterState.key }), _jsxs("div", { className: "flex h-[160px] w-full max-w-[300px] items-center justify-center", style: { background: '#1a1a1a' }, children: [adapterState.subPhase === 'fixation' && (_jsx("span", { className: "font-display text-[40px] text-cyan-surface", children: "+" })), showDigit && adapterState.stim && (_jsx("span", { className: "font-display text-[80px] text-cyan-surface", children: adapterState.stim.digit })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'absolute font-display text-[28px] font-medium',
                            adapterState.feedback === 'correct'
                                ? 'text-cyan-core'
                                : 'text-cyan-media',
                        ].join(' '), style: { marginTop: 110 }, children: adapterState.feedback === 'correct' ? '✓' : '✗' }))] }), _jsx("div", { className: "grid w-full max-w-[300px] grid-cols-4 gap-s2", children: (adapterState.stim?.options ?? []).map((symIdx, i) => (_jsx("button", { type: "button", onClick: () => handlePick(symIdx), disabled: !showOptions, "aria-label": t('symbol_coding.option_aria', { symbol: SYMBOLS_KEY[symIdx] }), className: [
                        'flex h-[64px] items-center justify-center border font-display text-[30px]',
                        'transition-all duration-feedback ease-soft-wind',
                        showOptions
                            ? 'cursor-pointer border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-bg active:scale-95'
                            : 'cursor-not-allowed border-cyan-border-faint bg-cyan-bg text-cyan-text-secondary opacity-50',
                    ].join(' '), children: SYMBOLS_KEY[symIdx] }, `opt-${i}-${symIdx}`))) }), _jsx("div", { className: "text-[11px] text-cyan-text-secondary", children: t('symbol_coding.footer_hint') })] }));
}
function recommendKey(rt) {
    if (rt === null)
        return 'symbol_coding.debrief.recommend_keep';
    if (rt < 900)
        return 'symbol_coding.debrief.recommend_excellent';
    if (rt < 1400)
        return 'symbol_coding.debrief.recommend_good';
    return 'symbol_coding.debrief.recommend_keep';
}
function median(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
        return Math.round(((sorted[mid - 1] + sorted[mid]) / 2));
    }
    return Math.round(sorted[mid]);
}
export function SymbolCodingDebriefView() {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const rtMedian = useMemo(() => {
        const rts = [];
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && typeof tt.response.reactionTimeMs === 'number') {
                rts.push(tt.response.reactionTimeMs);
            }
        }
        return median(rts);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const rtLabel = rtMedian === null ? '—' : String(rtMedian);
    const replay = () => {
        window.location.reload();
    };
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('symbol_coding.debrief.title'), scoreLabel: t('symbol_coding.debrief.score_label'), scoreValue: rtLabel, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
        ], recommendation: t(recommendKey(rtMedian)), scientificCaveat: t('symbol_coding.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map